import {
  createMoneyFormatter,
  escapeAttribute,
  escapeHtml,
  formatMoney,
  hiddenSalePrice,
  imageUrl,
  isSameProduct,
  isWhiteSwatch,
  metafieldValue,
  numericId,
  variantLabel,
} from '@lyle/algolia-product-utils'

let touchStartX = 0
let touchStartY = 0

document.addEventListener('click', event => {
  if (event.defaultPrevented) {
    return
  }

  const image = event.target.closest(
    '.product-card__primary-image, .product-card__secondary-image'
  )
  const imageCard = event.target.closest('[data-product-card]')

  if (
    image &&
    imageCard?.querySelector('.product-card__secondary-image') &&
    shouldRevealSecondaryImageOnClick(imageCard) &&
    !event.target.closest('[data-quick-view], [data-card-swatch], [data-card-size]')
  ) {
    event.preventDefault()
    showSecondaryImage(imageCard)
    return
  }

  const button = event.target.closest('[data-card-size]')

  if (!button) {
    return
  }

  event.preventDefault()
  void selectSize(button)
})

async function selectSize(button) {
  const productUrl = new URL(button.dataset.productUrl, window.location.origin)
  const size = button.dataset.sizeValue

  button.setAttribute('aria-busy', 'true')

  try {
    const response = await fetch(`${productUrl.pathname}.js`)
    const product = response.ok ? await response.json() : null
    const variant = product?.variants?.find(item => {
      return variantLabel(item).toLowerCase() === size.toLowerCase()
    })

    if (variant?.id) {
      productUrl.searchParams.set('variant', numericId(variant.id))
    }
  } catch {}

  window.location.href = productUrl.toString()
}

document.addEventListener('touchstart', event => {
  const thumbnail = event.target.closest('.product-card__thumbnail')

  if (!thumbnail || !event.touches.length) {
    return
  }

  touchStartX = event.touches[0].clientX
  touchStartY = event.touches[0].clientY
}, { passive: true })

document.addEventListener('touchend', event => {
  if (event.defaultPrevented) {
    return
  }

  const thumbnail = event.target.closest('.product-card__thumbnail')
  const card = event.target.closest('[data-product-card]')

  if (!thumbnail || !card || !event.changedTouches.length) {
    return
  }

  const touch = event.changedTouches[0]
  const deltaX = touch.clientX - touchStartX
  const deltaY = touch.clientY - touchStartY

  if (
    Math.abs(deltaX) <= 35 ||
    Math.abs(deltaX) <= Math.abs(deltaY) ||
    !card.querySelector('.product-card__secondary-image')
  ) {
    return
  }

  event.preventDefault()
  toggleSecondaryImage(card)
}, false)

function toggleSecondaryImage(card) {
  card.classList.toggle('product-card--show-secondary')
  card.dispatchEvent(new CustomEvent('product-card:image-toggle', {
    bubbles: true,
  }))
}

function showSecondaryImage(card) {
  card.classList.add('product-card--show-secondary')
  card.dispatchEvent(new CustomEvent('product-card:image-toggle', {
    bubbles: true,
  }))
}

function shouldRevealSecondaryImageOnClick(card) {
  if (card.classList.contains('product-card--show-secondary')) {
    return false
  }

  return window.matchMedia('(hover: none), (pointer: coarse)').matches
}

export class AlgoliaProductCardRenderer {
  #config
  #icons
  #isMobile
  #moneyFormatter

  constructor({
    config,
    icons = {},
    isMobile = () => false,
  }) {
    this.#config = config
    this.#icons = icons
    this.#isMobile = isMobile
    this.#moneyFormatter = this.#createMoneyFormatter()
  }

  render(product, index, options = {}) {
    const card = this.#cardViewModel(product, index, options)
    const imageAttrs = this.#imageOptionAttrs(options)

    return `
      <article class="product-card" data-product-card data-product-id="${this.#escapeHtml(card.id)}" data-product-index="${this.#escapeHtml(index)}" data-product-url="${this.#escapeHtml(card.url)}" data-product-handle="${this.#escapeHtml(card.handle)}" data-algolia-index="${this.#escapeHtml(card.indexName)}"${imageAttrs}>
        <div class="product-card__thumbnail">
          <a href="${this.#escapeHtml(card.url)}" data-product-link data-object-id="${this.#escapeHtml(card.objectID)}" data-query-id="${this.#escapeHtml(card.queryID)}" data-position="${this.#escapeHtml(card.position)}">
            ${this.#badgeMarkup(card.badge)}
            ${this.#imageMarkup(card.primaryImage, product.title, index, 'product-card__primary-image')}
            ${this.#imageMarkup(card.secondaryImage, '', index, 'product-card__secondary-image', { hidden: true })}
          </a>

          ${this.#quickAddMarkup(card)}

          <div class="product-card__sizes" data-product-card-sizes>${this.#sizesMarkup(product)}</div>
        </div>

        <div class="product-card__footer">
          <p class="product-card__title text-body-sm text-center">${this.#escapeHtml(product.title)}</p>

          ${product.featuredCollection ? `<p class="product-card__featured-collection text-eyebrow-2xs text-center">${this.#escapeHtml(product.featuredCollection)}</p>` : ''}
          ${this.#priceMarkup(product)}
          ${this.#memberMarkup(product)}

          <div class="product-card__swatches" data-product-card-swatches></div>
        </div>

        <script type="application/json" data-card-product>${this.#safeJson(this.#cardState(product))}</script>
      </article>
    `
  }

  renderSwatches(card, product, swatches) {
    const container = card.querySelector('[data-product-card-swatches]')

    if (!container || !swatches.length) {
      return
    }

    const visible = this.#visibleSwatches(swatches, product)
    const hiddenCount = swatches.length - visible.length

    container.innerHTML = `
      ${visible.map(swatch => this.#swatchMarkup(swatch, product)).join('')}
      ${hiddenCount > 0 ? `<a class="product-card__swatch-count text-eyebrow-2xs" href="${this.#escapeHtml(product.url)}">${this.#colourCountLabel(hiddenCount)}</a>` : ''}
    `
  }

  #cardViewModel(product, index, options = {}) {
    return {
      badge: this.#productBadge(product),
      handle: product.handle,
      id: product.id,
      indexName: product.__indexName,
      objectID: product.objectID || product.id,
      position: product.__position || index + 1,
      primaryImage: this.#primaryImage(product, options),
      queryID: product.__queryID || '',
      quickVariant: this.#quickVariant(product),
      secondaryImage: this.#secondaryImage(product, options),
      url: product.url,
    }
  }

  #imageOptionAttrs(options = {}) {
    if (options.imageOrder == null && options.useAltImage == null) {
      return ''
    }

    const imageOrder = options.imageOrder ?? this.#config.productCard?.imageOrder ?? 'modal'
    const useAltImage = options.useAltImage ?? this.#config.productCard?.useAltImage ?? false

    return ` data-image-order="${this.#escapeHtml(imageOrder)}" data-use-alt-image="${useAltImage ? 'true' : 'false'}"`
  }

  #imageOptions(options = {}) {
    return {
      imageOrder: options.imageOrder ?? this.#config.productCard?.imageOrder ?? 'modal',
      useAltImage: options.useAltImage ?? this.#config.productCard?.useAltImage ?? false,
    }
  }

  #quickVariant(product) {
    return (
      product.variantId ||
      product.variants?.find(variant => variant.available)?.id ||
      product.variants?.[0]?.id ||
      ''
    )
  }

  #badgeMarkup(badge) {
    if (!badge.label) {
      return ''
    }

    return `
      <span class="product-card__badge text-eyebrow-2xs ${badge.type === 'sale' ? 'product-card__badge--sale' : ''}" style="${this.#badgeStyle(badge)}">
        ${this.#escapeHtml(badge.label)}
      </span>
    `
  }

  #imageMarkup(image, alt, index, className, options = {}) {
    const { hidden = false } = options

    if (!image) {
      return ''
    }

    const altText = hidden ? '' : alt || image.alt || ''

    return `
      <span class="product-card__image-frame ${className}" ${hidden ? 'aria-hidden="true"' : ''}>
        <img
          class="product-card__image"
          src="${this.#escapeHtml(this.#imageUrl(image, 900))}"
          srcset="${this.#escapeHtml(this.#imageSrcset(image))}"
          sizes="(width >= 1024px) 25vw, 50vw"
          alt="${this.#escapeHtml(altText)}"
          loading="${hidden || index >= 4 ? 'lazy' : 'eager'}"
          decoding="async"
          ${!hidden && index < 4 ? 'fetchpriority="high"' : ''}
          ${image.width ? `width="${this.#escapeHtml(image.width)}"` : ''}
          ${image.height ? `height="${this.#escapeHtml(image.height)}"` : ''}
        >
      </span>
    `
  }

  #quickAddMarkup(card) {
    if (this.#config.productCard?.showQuickAdd === false) {
      return ''
    }

    if (!card.quickVariant) {
      return ''
    }

    return `
      <button class="product-card__quick-add-mobile" type="button" data-quick-view data-variant-id="${this.#escapeHtml(card.quickVariant)}" data-object-id="${this.#escapeHtml(card.objectID)}" data-query-id="${this.#escapeHtml(card.queryID)}" aria-label="${this.#escapeHtml(this.#config.strings.quickAdd)}">
        ${this.#icon('bag')}
      </button>
    `
  }

  #colourCountLabel(count) {
    const template = count === 1
      ? this.#config.strings.colourCountOne
      : this.#config.strings.colourCountOther

    return this.#escapeHtml(
      String(template || `+${count}`).replace('__count__', String(count))
    )
  }

  #priceMarkup(product) {
    const memberDiscount = Number(
      this.#metafieldValue(product, 'member_discount_percentage') || 0
    )
    const membersOnly =
      this.#metafieldValue(product, 'members_only_purchase') === true
    const salePrice = hiddenSalePrice(product.tags, this.#config.market.currencyCode)
    const price = salePrice ?? product.price?.amount ?? 0
    const compareAtPrice = salePrice !== null
      ? product.price?.amount
      : product.compareAtPrice?.amount
    const onSale = compareAtPrice && price < compareAtPrice

    if (
      !this.#config.memberPricing.isCustomer &&
      memberDiscount &&
      membersOnly
    ) {
      return `<p class="product-card__membership-text text-body-sm">${this.#escapeHtml(this.#config.strings.memberPricing.onlyMembers)}</p>`
    }

    const baseMarkup = `
      <p class="product-prices product-card__prices text-body-sm text-center">
        ${onSale ? `<span class="product-prices__compare-at-price">${this.#formatMoney(compareAtPrice)}</span>` : ''}
        <span class="product-prices__price ${onSale ? 'product-prices__price--on-sale' : ''}">${this.#formatMoney(price)}</span>
        ${product.available === false ? `<span class="product-prices__sold-out">${this.#escapeHtml(this.#config.strings.soldOut || '')}</span>` : ''}
      </p>
    `

    if (!this.#config.memberPricing.isCustomer || !memberDiscount) {
      return baseMarkup
    }

    const memberPrice = salePrice ?? price - price * memberDiscount

    return `
      <div class="product-card__prices-container">
        <span class="product-card__original-price text-body-xs">${this.#escapeHtml(this.#config.strings.memberPricing.originalPrice)}</span>
        ${baseMarkup}
      </div>

      <div class="product-card__membership-badge">
        ${this.#icon('eagle')}
        <span class="text-body-xs">${this.#escapeHtml(this.#config.strings.memberPricing.memberPrice)} ${this.#formatMoney(memberPrice)}</span>
      </div>
    `
  }

  #memberMarkup(product) {
    const memberDiscount = Number(
      this.#metafieldValue(product, 'member_discount_percentage') || 0
    )

    if (this.#config.memberPricing.isCustomer || !memberDiscount) {
      return ''
    }

    return `
      <div class="product-card__membership-badge">
        ${this.#icon('eagle')}
        <span class="text-body-xs">${this.#escapeHtml(this.#config.strings.memberPricing.exclusive)}</span>
      </div>
    `
  }

  #productBadge(product) {
    const strings = this.#config.strings.badges
    const badgeTag =
      product.tags?.find(tag => tag.toLowerCase().startsWith('badge:')) || ''
    let [label = '', color = '#111111', bgColor = '#f9f9f9'] = badgeTag
      .replace(/badge:/i, '')
      .split(':')
      .map(part => part.trim())
    let type = 'badge'
    let isSaleBadge = this.#isSaleBadgeLabel(label)

    if ((product.tags || []).includes('coming-soon') && !label) {
      label = strings.comingSoon
    }

    switch (label.toLowerCase()) {
      case 'bestseller':
      case 'best seller':
        label = strings.bestseller
        break
      case 'coming soon':
      case 'coming-soon':
        label = strings.comingSoon
        break
      case 'new':
      case 'new in':
        label = strings.newIn
        break
      case 'sale':
        label = strings.sale
        type = 'sale'
        isSaleBadge = true
        break
      default:
        if (/\d+% off/i.test(label)) {
          isSaleBadge = true
          label = label.replace(/(\d+)% off/i, (_, percent) =>
            strings.percentOff.replace('__percent__', percent)
          )
        }
        break
    }

    if (this.#config.market?.restrictSaleBadges && isSaleBadge) {
      return { label: '', color, bgColor, type }
    }

    return { label, color, bgColor, type }
  }

  #isSaleBadgeLabel(label) {
    const normalisedLabel = String(label || '').trim().toLowerCase()

    return normalisedLabel === 'sale' || /\d+\s*%\s*off/i.test(normalisedLabel)
  }

  #badgeStyle(badge) {
    return `color: ${this.#escapeHtml(badge.color)}; background-color: ${this.#escapeHtml(badge.bgColor)};`
  }

  #primaryImage(product, options = {}) {
    const { imageOrder, useAltImage } = this.#imageOptions(options)
    const images = this.#imagesWithoutAlt(product)
    const altImage = this.#altLandingImage(product)

    if (useAltImage && altImage) {
      return altImage
    }

    if (imageOrder === 'flat') {
      return images[images.length - 1] || product.images?.[0] || null
    }

    return images[0] || product.images?.[0] || null
  }

  #secondaryImage(product, options = {}) {
    const { imageOrder, useAltImage } = this.#imageOptions(options)

    if (useAltImage) {
      return null
    }

    const images = this.#imagesWithoutAlt(product)

    if (images.length < 2) {
      return null
    }

    if (imageOrder === 'flat') {
      return images[0]
    }

    return images[images.length - 1]
  }

  #altLandingImage(product) {
    return (product.images || []).find((image) =>
      String(image.alt || '').includes('alt_landing_image')
    )
  }

  #imagesWithoutAlt(product) {
    return (product.images || []).filter(
      (image) => !String(image.alt || '').includes('alt_landing_image')
    )
  }

  #swatchMarkup(swatch, product) {
    const color = this.#metafieldValue(swatch, 'swatch_code') || '#ffffff'
    const active = this.#isSameProduct(swatch, product)

    return `
      <button
        class="product-card__swatch ${active ? 'is-active' : ''} ${isWhiteSwatch(color) ? 'product-card__swatch--white' : ''}"
        type="button"
        data-card-swatch
        data-product='${this.#escapeAttribute(JSON.stringify(swatch))}'
        aria-label="${this.#escapeHtml(swatch.title)}"
      >
        <span class="product-card__swatch-inner" style="--swatch-color: ${this.#escapeHtml(color)}"></span>
      </button>
    `
  }

  #variantSizeMarkup(variant, product) {
    const label = this.#variantLabel(variant)
    const id = this.#numericId(variant.id)

    if (!variant.available) {
      return `
        <span class="product-card__size text-eyebrow-md" aria-disabled="true">
          ${this.#escapeHtml(label)}
        </span>
      `
    }

    return `
      <a
        class="product-card__size text-eyebrow-md"
        href="${this.#escapeHtml(`${product.url}?variant=${id}`)}"
        data-product-link
        data-object-id="${this.#escapeHtml(product.objectID || product.id)}"
        data-query-id="${this.#escapeHtml(product.__queryID || '')}"
        data-position="${this.#escapeHtml(product.__position || '')}"
      >
        ${this.#escapeHtml(label)}
      </a>
    `
  }

  // Products that come in one size have nothing to choose, so the size overlay
  // is left empty and the card links straight to the product.
  #sizesMarkup(product) {
    const variants = product.variants || []

    if (variants.length > 1) {
      const labels = new Set(variants.map(variant => this.#variantLabel(variant)))

      if (labels.size < 2) {
        return ''
      }

      return variants
        .map(variant => this.#variantSizeMarkup(variant, product))
        .join('')
    }

    // Only `sizes` is the full chart. `sizesAvailable` is the in-stock subset, so
    // a single entry there means one size left, not a one-size product.
    if (product.sizes?.length) {
      if (product.sizes.length < 2) {
        return ''
      }

      return product.sizes
        .map(size => this.#fallbackSizeMarkup(size, product))
        .join('')
    }

    return (product.sizesAvailable || [])
      .map(size => this.#fallbackSizeMarkup(size, product))
      .join('')
  }

  #fallbackSizeMarkup(size, product) {
    if (Number(product.sizeStock?.[size]) === 0) {
      return `
        <span class="product-card__size text-eyebrow-md" aria-disabled="true">
          ${this.#escapeHtml(size)}
        </span>
      `
    }

    return `
      <button
        class="product-card__size text-eyebrow-md"
        type="button"
        data-card-size
        data-product-link
        data-product-url="${this.#escapeHtml(product.url)}"
        data-size-value="${this.#escapeHtml(size)}"
        data-object-id="${this.#escapeHtml(product.objectID || product.id)}"
        data-query-id="${this.#escapeHtml(product.__queryID || '')}"
        data-position="${this.#escapeHtml(product.__position || '')}"
      >
        ${this.#escapeHtml(size)}
      </button>
    `
  }

  #visibleSwatches(swatches, product) {
    const max = this.#isMobile() ? 3 : 8
    const visible = swatches.slice(0, max)
    const activeSwatch = swatches.find(swatch =>
      this.#isSameProduct(swatch, product)
    )

    if (
      !activeSwatch ||
      visible.some(swatch => this.#isSameProduct(swatch, product))
    ) {
      return visible
    }

    return [
      activeSwatch,
      ...swatches.filter(swatch => !this.#isSameProduct(swatch, activeSwatch)),
    ].slice(0, max)
  }

  #variantLabel(variant) {
    return variantLabel(variant)
  }

  #metafieldValue(product, name) {
    return metafieldValue(product, name)
  }

  #isSameProduct(productA, productB) {
    return isSameProduct(productA, productB)
  }

  #cardState(product) {
    return {
      available: product.available,
      compareAtPrice: product.compareAtPrice,
      featuredCollection: product.featuredCollection,
      handle: product.handle,
      id: product.id,
      images: product.images || [],
      metafields: product.metafields || [],
      objectID: product.objectID,
      price: product.price,
      sizeStock: product.sizeStock,
      sizes: product.sizes,
      sizesAvailable: product.sizesAvailable,
      tags: product.tags || [],
      title: product.title,
      url: product.url,
      variantId: product.variantId,
      variants: product.variants || [],
      __indexName: product.__indexName,
      __position: product.__position,
      __queryID: product.__queryID,
    }
  }

  #imageSrcset(image) {
    const widths = [200, 300, 400, 500, 700, 900]

    return widths
      .map(width => `${this.#imageUrl(image, width)} ${width}w`)
      .join(', ')
  }

  #imageUrl(image, width) {
    return imageUrl(image, width)
  }

  #createMoneyFormatter() {
    return createMoneyFormatter(this.#config.market)
  }

  #formatMoney(value) {
    return formatMoney(value, {
      currencySymbol: this.#config.market.currencySymbol,
      formatter: this.#moneyFormatter,
    })
  }

  #numericId(id) {
    return numericId(id)
  }

  #icon(name) {
    return this.#icons[name] || ''
  }

  #safeJson(value) {
    return JSON.stringify(value).replace(/<\/script/gi, '<\\/script')
  }

  #escapeHtml(value) {
    return escapeHtml(value)
  }

  #escapeAttribute(value) {
    return escapeAttribute(value)
  }
}
